/* DYNAMO · Kaufleiste unten <dy-kaufleiste> (06.10.2026, Auftrag Stefan, Vorbild PREMIUMMATTEN).
   Markup: snippets/dy-leiste-kauf.liquid (Sektion sections/dy-leiste-kauf.liquid).
   - Sichtbar, sobald der Haupt-Kaufknopf nach oben aus dem Bild gescrollt ist (IntersectionObserver unter der festen Kopfzeile,
     dazu eine leichte Scroll-Prüfung für Sprünge über den Knopf hinweg); wieder weg, wenn er sichtbar ist oder die Fußzeile
     erreicht ist. Offene Schublade, Menü oder Dialoge blenden sie per CSS aus.
     Der Zustand steht an <html data-dy-kaufleiste>, damit er Neuzeichnungen von Horizon (morph) übersteht.
   - Sets: Knopf scrollt weich zur Set-Auswahl, hebt sie kurz hervor und setzt den Fokus auf die gewählte Kachel.
   - Sonst: Klick auf den echten Kaufknopf des Hauptformulars. So gelten Menge (Mengen-Kacheln), Schublade und Mengenrabatt.
   - Variantenwechsel: Bild, Variante, Preis und Knopf kommen aus der Seite, die Horizon ohnehin lädt (ProductSelectEvent). */
import { StandardEvents } from '@shopify/events';
import { ThemeEvents } from '@theme/events';

const AN = 'data-dy-kaufleiste';
const PULS_MS = 2200;
const PUPPET_MS = 6000;
const FERTIG_MS = 2000;

class DYKaufleiste extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abbruch;
  /** @type {IntersectionObserver | undefined} */
  #beobKauf;
  /** @type {IntersectionObserver | undefined} */
  #beobFuss;
  /** @type {Element | null} */
  #ziel = null;
  /** @type {Element | null} */
  #fuss = null;
  #rafWartet = false;
  #kaufOben = false;
  #fussSichtbar = false;
  #editor = false;
  #menge = 1;
  #kopf = -1;
  #wartet = false;
  /** @type {number | undefined} */
  #puppetTimer;
  /** @type {number | undefined} */
  #fertigTimer;
  /** @type {number | undefined} */
  #pulsTimer;
  /** @type {number | undefined} */
  #groesseTimer;

  connectedCallback() {
    this.#abbruch = new AbortController();
    const { signal } = this.#abbruch;
    this.addEventListener('click', this.#beiKlick, { signal });
    document.addEventListener(StandardEvents.productSelect, this.#beiVariante, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#beiMenge, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#beiWarenkorb, { signal });
    document.addEventListener(StandardEvents.cartError, this.#beiFehler, { signal });
    window.addEventListener('resize', this.#beiGroesse, { signal, passive: true });
    // Horizon scrollt am Computer in .page-wrapper, am Handy im Fenster: Erfassen in der Capture-Phase deckt beides ab
    document.addEventListener('scroll', this.#beiScroll, { signal, passive: true, capture: true });
    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:section:select', this.#beiEditor, { signal });
      document.addEventListener('shopify:section:deselect', this.#beiEditor, { signal });
    }
    // Module laufen erst nach dem Aufbau der Seite: Hauptformular und Fußzeile sind schon da
    this.#beobachten();
    this.#randMessen();
    this.#mengeLesen();
  }

  disconnectedCallback() {
    this.#abbruch?.abort();
    this.#beobKauf?.disconnect();
    this.#beobFuss?.disconnect();
    [this.#puppetTimer, this.#fertigTimer, this.#pulsTimer, this.#groesseTimer].forEach((t) => clearTimeout(t));
    document.documentElement.removeAttribute(AN);
  }

  /* ---------- Hauptformular ---------- */

  #formular() {
    const id = this.dataset.productId;
    return (
      document.querySelector(`#MainContent product-form-component[data-product-id="${id}"]`) ||
      document.querySelector('#MainContent product-form-component')
    );
  }

  /** @returns {HTMLButtonElement | null} */
  #kaufknopf() {
    const knopf = this.#formular()?.querySelector('button[ref="addToCartButton"]');
    return knopf instanceof HTMLButtonElement ? knopf : null;
  }

  /* ---------- Sichtbarkeit ---------- */

  #kopfHoehe() {
    const kopf = document.querySelector('#header-component');
    if (!(kopf instanceof HTMLElement)) return 0;
    const sticky = kopf.getAttribute('sticky');
    if (!sticky || sticky === 'never') return 0;
    return Math.max(0, Math.min(Math.round(kopf.getBoundingClientRect().height), 160));
  }

  #beobachten() {
    this.#beobKauf?.disconnect();
    this.#beobFuss?.disconnect();
    const formular = this.#formular();
    this.#ziel = formular?.closest('.buy-buttons-block') || formular || null;
    this.#fuss = document.querySelector('.page-wrapper > footer') || document.querySelector('footer');
    if (!this.#ziel) return;
    this.#kopf = this.#kopfHoehe();
    // IntersectionObserver meldet jeden Wechsel (auch Größenänderungen über dem Kaufknopf) …
    this.#beobKauf = new IntersectionObserver(() => this.#pruefen(), {
      rootMargin: `-${this.#kopf}px 0px 0px 0px`,
      threshold: 0,
    });
    this.#beobKauf.observe(this.#ziel);
    if (this.#fuss) {
      this.#beobFuss = new IntersectionObserver(() => this.#pruefen(), { threshold: 0 });
      this.#beobFuss.observe(this.#fuss);
    }
    this.#pruefen();
  }

  /** … und die Scroll-Prüfung fängt Sprünge ab, bei denen der Knopf nie im Bild war (Sprunglink, schnelles Wischen). */
  #beiScroll = () => {
    if (this.#rafWartet) return;
    this.#rafWartet = true;
    requestAnimationFrame(() => {
      this.#rafWartet = false;
      this.#pruefen();
    });
  };

  #pruefen() {
    const ziel = this.#ziel;
    if (!ziel || !ziel.isConnected) {
      if (ziel) this.#beobachten();
      return;
    }
    const r = ziel.getBoundingClientRect();
    // nur „nach oben hinausgescrollt“ zählt, nicht „noch nicht erreicht“
    this.#kaufOben = r.height > 0 && r.bottom <= this.#kopf + 1;
    const f = this.#fuss && this.#fuss.isConnected ? this.#fuss.getBoundingClientRect() : null;
    this.#fussSichtbar = Boolean(f && f.height > 0 && f.top < window.innerHeight && f.bottom > 0);
    this.#anzeigen();
  }

  #anzeigen() {
    const an = this.#editor || (this.#kaufOben && !this.#fussSichtbar);
    const html = document.documentElement;
    if (html.hasAttribute(AN) !== an) html.toggleAttribute(AN, an);
  }

  /** Breite der Bildlaufleiste (am Computer scrollt Horizon in .page-wrapper), damit die Leiste sie nicht verdeckt */
  #randMessen() {
    const pw = document.querySelector('.page-wrapper');
    const sb = pw instanceof HTMLElement ? Math.max(0, pw.offsetWidth - pw.clientWidth) : 0;
    this.style.setProperty('--dy-kl-sb', `${sb}px`);
  }

  #beiGroesse = () => {
    clearTimeout(this.#groesseTimer);
    this.#groesseTimer = setTimeout(() => {
      this.#randMessen();
      if (Math.abs(this.#kopfHoehe() - this.#kopf) > 4) this.#beobachten();
      else this.#pruefen();
    }, 150);
  };

  /** @param {any} event */
  #beiEditor = (event) => {
    if (event.detail?.sectionId !== this.dataset.sectionId) return;
    this.#editor = event.type === 'shopify:section:select';
    this.#anzeigen();
  };

  /* ---------- Klicks ---------- */

  /** @param {MouseEvent} event */
  #beiKlick = (event) => {
    const ziel = event.target instanceof Element ? event.target.closest('[data-dy-kl-aktion]') : null;
    if (!(ziel instanceof HTMLElement) || !this.contains(ziel)) return;
    const aktion = ziel.dataset.dyKlAktion;
    if (aktion === 'set') this.#zurAuswahl('set');
    else if (aktion === 'aendern') this.#zurAuswahl(ziel.dataset.dyKlZiel || '');
    else if (aktion === 'kauf' && ziel instanceof HTMLButtonElement) this.#kaufen(ziel);
  };

  /**
   * Scrollt weich zur Auswahl (Set, Farbe …), hebt sie kurz hervor und setzt den Fokus auf die gewählte Kachel.
   * @param {string} name - Optionsname klein (data-dy-option), leer = erste Option bzw. Kaufbereich
   */
  #zurAuswahl(name) {
    const haupt = document.querySelector('#MainContent');
    const picker =
      haupt?.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`) ||
      haupt?.querySelector('variant-picker');
    /** @type {Element | null | undefined} */
    let ziel = null;
    if (picker && name) {
      ziel = Array.from(picker.querySelectorAll('[data-dy-option]')).find((el) => el.getAttribute('data-dy-option') === name);
    }
    if (!ziel && picker) ziel = picker.querySelector('fieldset, .variant-option') || picker;
    if (!ziel) ziel = this.#formular()?.closest('.buy-buttons-block') || this.#formular();
    if (!(ziel instanceof HTMLElement)) return;

    const ruhig = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    ziel.scrollIntoView({ behavior: ruhig ? 'auto' : 'smooth', block: 'center' });
    const puls = ziel;
    puls.classList.remove('dy-kl-puls');
    void puls.offsetWidth; // Animation neu starten
    puls.classList.add('dy-kl-puls');
    clearTimeout(this.#pulsTimer);
    this.#pulsTimer = setTimeout(() => puls.classList.remove('dy-kl-puls'), PULS_MS);

    const feld = ziel.querySelector('input:checked') || ziel.querySelector('input, select, button:not([disabled])');
    if (feld instanceof HTMLElement) feld.focus({ preventScroll: true });
  }

  /** @param {HTMLButtonElement} knopfLeiste */
  #kaufen(knopfLeiste) {
    if (knopfLeiste.disabled || knopfLeiste.getAttribute('aria-busy') === 'true') return;
    const knopf = this.#kaufknopf();
    if (!knopf || knopf.disabled) {
      // Hauptformular nicht bereit (z. B. Kombination nicht lieferbar): zur Auswahl statt still nichts tun
      this.#zurAuswahl('');
      return;
    }
    // wie Horizons eigene Leiste: „puppet“ unterdrückt die Animation am (unsichtbaren) Hauptknopf
    knopf.dataset.puppet = 'true';
    clearTimeout(this.#puppetTimer);
    this.#puppetTimer = setTimeout(() => this.#fertig(false), PUPPET_MS);
    this.#wartet = true;
    knopfLeiste.setAttribute('aria-busy', 'true');
    knopf.click();
  }

  /** @param {boolean} ok */
  #fertig(ok) {
    this.#wartet = false;
    clearTimeout(this.#puppetTimer);
    const knopf = this.#kaufknopf();
    if (knopf) knopf.dataset.puppet = 'false';
    const kl = this.querySelector('[data-dy-kl-aktion="kauf"]');
    if (!kl) return;
    kl.removeAttribute('aria-busy');
    if (!ok) return;
    kl.setAttribute('data-fertig', '');
    clearTimeout(this.#fertigTimer);
    this.#fertigTimer = setTimeout(() => this.querySelector('[data-dy-kl-aktion="kauf"]')?.removeAttribute('data-fertig'), FERTIG_MS);
  }

  /** @param {Event} event */
  #ausHauptformular(event) {
    const t = event.target;
    return t instanceof Element && Boolean(t.closest('#MainContent product-form-component'));
  }

  /** @param {any} event - CartLinesUpdateEvent */
  #beiWarenkorb = (event) => {
    if (!this.#wartet || !this.#ausHauptformular(event)) return;
    const versprechen = event.promise;
    if (versprechen && typeof versprechen.then === 'function') {
      versprechen
        .then((/** @type {any} */ erg) => this.#fertig(!erg?.detail?.didError))
        .catch(() => this.#fertig(false));
    } else {
      this.#fertig(true);
    }
  };

  /** @param {any} event - CartErrorEvent */
  #beiFehler = (event) => {
    if (!this.#wartet) return;
    this.#fertig(false);
    if (!this.#ausHauptformular(event)) return;
    // Horizon zeigt den Fehler am Hauptknopf: dorthin scrollen, damit er sichtbar ist
    const ruhig = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#formular()?.scrollIntoView({ behavior: ruhig ? 'auto' : 'smooth', block: 'center' });
  };

  /* ---------- Menge (Mengen-Kacheln bzw. Horizon-Mengenfeld) ---------- */

  /** @param {any} event - QuantitySelectorUpdateEvent */
  #beiMenge = (event) => {
    if (event.detail?.cartLine) return; // Warenkorb, nicht Produktseite
    if (!this.#ausHauptformular(event)) return;
    const n = Number(event.detail?.quantity);
    this.#menge = Number.isFinite(n) && n > 0 ? n : 1;
    this.#mengeZeigen();
  };

  #mengeLesen() {
    const feld = this.#formular()?.querySelector('input[name="quantity"]');
    const n = feld instanceof HTMLInputElement ? Number(feld.value) : 1;
    this.#menge = Number.isFinite(n) && n > 0 ? n : 1;
    this.#mengeZeigen();
  }

  #mengeZeigen() {
    const el = this.querySelector('[data-dy-kl-menge]');
    if (!(el instanceof HTMLElement)) return;
    const mehr = this.#menge > 1;
    el.hidden = !mehr;
    el.textContent = mehr ? `${this.#menge} Stück` : '';
  }

  /* ---------- Variantenwechsel ---------- */

  /** @param {any} event - ProductSelectEvent */
  #beiVariante = (event) => {
    const t = event.target;
    if (!(t instanceof Element) || t.closest('product-card') || !t.closest('#MainContent')) return;
    event.promise
      ?.then((/** @type {any} */ erg) => {
        const detail = erg?.detail;
        const html = detail?.html;
        if (!html) return;
        if (detail.productId && String(detail.productId) !== String(this.dataset.productId)) return;
        const neu =
          html.querySelector(`dy-kaufleiste[data-product-id="${this.dataset.productId}"]`) || html.querySelector('dy-kaufleiste');
        const innenNeu = neu?.querySelector('[data-dy-kl-innen]');
        const innen = this.querySelector('[data-dy-kl-innen]');
        if (!neu || !innenNeu || !innen) return;
        const fokus = document.activeElement instanceof HTMLElement && innen.contains(document.activeElement)
          ? document.activeElement.getAttribute('data-dy-kl-aktion')
          : null;
        innen.replaceWith(document.importNode(innenNeu, true));
        this.dataset.modus = neu.getAttribute('data-modus') || this.dataset.modus || 'kauf';
        this.dataset.verfuegbar = neu.getAttribute('data-verfuegbar') || 'false';
        this.#mengeZeigen();
        if (fokus) {
          const wieder = this.querySelector(`[data-dy-kl-aktion="${fokus}"]`);
          if (wieder instanceof HTMLElement) wieder.focus({ preventScroll: true });
        }
      })
      .catch((/** @type {any} */ fehler) => {
        if (fehler?.name !== 'AbortError') console.warn('[dy-kaufleiste] Variantenwechsel:', fehler);
      });
  };
}

if (!customElements.get('dy-kaufleiste')) {
  customElements.define('dy-kaufleiste', DYKaufleiste);
}
